// ModelDuplicateModal.jsx
//
// Small modal for duplicating a model. Just prompts for the new name
// (with optional description override) and calls the duplicate endpoint
// which atomically copies model + channel personas + base curves +
// feature modifiers + funnel params + behavioral personas.

import React, { useEffect, useState } from "react";
import {
  Alert, Box, Button, Dialog, DialogActions,
  DialogContent, DialogTitle, TextField, Typography,
} from "@mui/material";

import { duplicateForecastModel } from "../../../services/forecast/forecastservices";

const AMBER = "#BA7517";


export default function ModelDuplicateModal({ open, sourceModel, onClose, onSaved }) {
  const [name,        setName]        = useState("");
  const [description, setDescription] = useState("");
  const [saving,      setSaving]      = useState(false);
  const [error,       setError]       = useState("");

  useEffect(() => {
    if (!open) return;
    setName(sourceModel ? `${sourceModel.name} (copy)` : "");
    setDescription("");
    setError("");
  }, [open, sourceModel]);

  const handleSave = async () => {
    if (!name.trim()) { setError("Name is required"); return; }

    setSaving(true); setError("");
    try {
      await duplicateForecastModel(sourceModel.id, {
        name:        name.trim(),
        description: description.trim() || undefined,
      });
      onSaved?.();
      onClose();
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || "Failed to duplicate");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontSize: 16, fontWeight: 500 }}>
        Duplicate Model
      </DialogTitle>

      <DialogContent dividers sx={{ py: 2 }}>
        {sourceModel && (
          <Box sx={{ mb: 2 }}>
            <Typography sx={{ fontSize: 11, color: "text.secondary",
              textTransform: "uppercase", letterSpacing: "0.05em" }}>
              Duplicating
            </Typography>
            <Typography sx={{ fontSize: 13, fontWeight: 500 }}>
              {sourceModel.name}
            </Typography>
            <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 0.5 }}>
              All channel personas, base curves, feature modifiers, funnel
              params, and behavioral personas will be copied to the new model.
            </Typography>
          </Box>
        )}

        <TextField
          label="New name" required size="small" fullWidth autoFocus
          value={name}
          onChange={e => setName(e.target.value)}
          disabled={saving}
          sx={{ mb: 2 }}
        />

        <TextField
          label="Description override (optional)" size="small" fullWidth multiline rows={2}
          value={description}
          onChange={e => setDescription(e.target.value)}
          disabled={saving}
          helperText="Leave blank to copy the source description"
        />

        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={saving} sx={{ textTransform: "none" }}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={saving}
          variant="contained"
          sx={{
            textTransform: "none", borderRadius: "20px",
            background: AMBER, "&:hover": { background: "#9A6014" },
          }}
        >
          {saving ? "Duplicating…" : "Duplicate"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}